import {
  Display,
  Heading,
  PromoBannerV2,
  ShadowToken,
  SpacingToken,
  Text,
  TransitionDurationToken,
  TransitionTimingToken,
} from '@livechat/design-system-react-components'
import { css } from '@emotion/react'
import { Link } from 'gatsby'
import { useCallback, useMemo } from 'react'

import {
  AMPLITUDE_EVENTS,
  CAROUSEL_TYPES,
} from 'gatsby-theme-marketplace-base/src/constants'

import type { Slide } from 'gatsby-theme-marketplace-base/src/components/Carousel/interfaces'
import { Carousel } from 'gatsby-theme-marketplace-base/src/components/Carousel/Carousel'
import { CarouselItem } from 'gatsby-theme-marketplace-base/src/components/Carousel/CarouselItem'
import { useTypedDispatch } from 'gatsby-theme-marketplace-base/src/store/hooks'
// @ts-expect-error
import { logAmplitudeEvent } from 'gatsby-theme-marketplace-base/src/store/stats'

import { FEATURED_APP_CATEGORIES } from '../../constants/collections'

const IMAGE_WIDTH = 110

const headingCss = css`
  margin-block: 0 var(${SpacingToken.Spacing4});
`

const nameClassResetCss = css`
  .name {
    max-height: unset !important;
    overflow: unset !important;
    margin-bottom: unset !important;
  }
`

const collectionWrapperCss = css`
  ${nameClassResetCss}
`

const slideWrapperCss = css`
  .custom-banner > div {
    flex-direction: row;
  }
`

const bannerCss = (gradient?: string) => css`
  ${gradient && `background: ${gradient};`}
  background: ${gradient};
  width: 366px;
  height: 136px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  user-select: none;
  transition: box-shadow var(${TransitionDurationToken.Fast2})
    var(${TransitionTimingToken.EaseIn});

  &:hover {
    box-shadow: var(${ShadowToken.Float});
  }

  @container (max-width: 559px) {
    flex-direction: row !important;
  }

  padding: var(${SpacingToken.Spacing4}) var(${SpacingToken.Spacing5})
    var(${SpacingToken.Spacing5});

  .banner {
    &__content {
      ${contentCss}
    }

    &__additional-content {
      ${additionalContentCss}
    }
  }
`

const contentCss = css`
  flex: 1 1;
  padding: 0;
`

const additionalContentCss = css`
  padding: 0;
  height: 100%;
  order: 2;
  padding: 0;
  flex: 0 0 ${IMAGE_WIDTH}px;
  justify-content: center;
  align-items: center;
`

const textCss = css`
  text-align: left;
  margin: 0;
`

const descriptionCss = css`
  ${textCss};
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
`

const displayCss = css`
  ${textCss}
  margin-bottom: var(${SpacingToken.Spacing1});
`

const carouselCss = css`
  ul {
    height: 156px;
    padding: 0 var(${SpacingToken.Spacing2}) 0 0;
    margin: 0 calc(var(${SpacingToken.Spacing2}) * -1) 0 0;
  }
`

export const Collections = () => {
  const dispatch = useTypedDispatch()

  const getBannerSlides = useCallback((): Slide[] => {
    const slides = FEATURED_APP_CATEGORIES.map(
      ({ label, description, url, gradient, image }) => {
        const onNavigate = () => {
          dispatch(
            logAmplitudeEvent({
              name: `${AMPLITUDE_EVENTS.COLLECTION_CLICKED} - ${label}`,
            })
          )
        }

        return {
          type: CAROUSEL_TYPES.PROMO_BANNER,
          component: (
            <Link css={slideWrapperCss} to={url} onClick={onNavigate}>
              <PromoBannerV2
                css={bannerCss(gradient)}
                additionalContent={<img src={image} alt={label} />}
                className="custom-banner"
                contentClassName="banner__content"
                additionalContentClassName="banner__additional-content"
              >
                <Display size="sm" css={displayCss}>
                  {label}
                </Display>

                <Text css={descriptionCss}>{description}</Text>
              </PromoBannerV2>
            </Link>
          ),
          id: label,
        }
      }
    )

    return slides
  }, [dispatch])

  const slides = useMemo(() => getBannerSlides(), [getBannerSlides])

  return (
    <div css={collectionWrapperCss}>
      <Heading css={headingCss}>Collections</Heading>

      <Carousel
        css={carouselCss}
        items={slides}
        renderItem={({ item, isSnapPoint }) => (
          <CarouselItem key={item.id} isSnapPoint={isSnapPoint}>
            <div>{item.component}</div>
          </CarouselItem>
        )}
      />
    </div>
  )
}
